// AlunosView — lista de alunos da company + drawer com detalhe

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Ban,
  CalendarCheck,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Copy,
  CreditCard,
  ExternalLink,
  Library,
  Loader2,
  Receipt,
  Search,
  UserRound,
  Users,
  X
} from "lucide-react";
import { api, ApiError, type StudentDetail, type StudentRow } from "../api";
import { openAlert, openConfirm } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const students = signal<StudentRow[] | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const detail = signal<StudentDetail | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listStudents();
    students.value = r.students;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}
async function openDetail(uid: string) {
  try {
    const r = await api.getStudent(uid);
    detail.value = r;
  } catch (e) {
    await openAlert({
      title: "Não foi possível carregar o aluno",
      message: friendlyError(e),
      danger: true
    });
  }
}

export function AlunosView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading")
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  if (phase.value === "error")
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" /> {errMsg.value}
      </div>
    );
  const rows = students.value ?? [];
  const q = search.value.toLowerCase().trim();
  const visible = useMemo(() => {
    return rows.filter((s) => {
      if (q) {
        const hay = `${s.email} ${s.name} ${s.apelido ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, q]);

  return (
    <div class="space-y-4">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <UserRound className="size-6 text-primary" />
          Alunos
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          {rows.length} aluno{rows.length !== 1 ? "s" : ""} vinculado{rows.length !== 1 ? "s" : ""}.
        </p>
      </header>
      <div class="flex items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            placeholder="Buscar email, nome…"
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
      </div>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhum aluno encontrado.
        </div>
      ) : (
        <div class="overflow-hidden rounded-md border border-border bg-card/40">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <th class="px-3 py-2">Aluno</th>
                <th class="px-3 py-2 text-right">Turmas ativas</th>
                <th class="px-3 py-2">Status</th>
                <th class="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => (
                <tr key={s.userId} class="border-b border-border/60 last:border-0 hover:bg-accent/30">
                  <td class="px-3 py-2">
                    <div class="font-semibold">{s.name}</div>
                    <div class="font-mono text-[11px] text-muted-foreground">{s.email}</div>
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums">{s.turmasAtivas}</td>
                  <td class="px-3 py-2">
                    {s.suspended ? (
                      <span class="inline-flex items-center gap-1 rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-600">
                        <Ban width="0.85em" height="0.85em" /> Suspenso
                      </span>
                    ) : (
                      <span class="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-700 dark:text-emerald-300">
                        <CheckCircle2 width="0.85em" height="0.85em" /> Ativo
                      </span>
                    )}
                  </td>
                  <td class="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => openDetail(s.userId)}
                      class="rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-accent"
                    >
                      Detalhe
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail.value && <Drawer />}
    </div>
  );
}

function Drawer() {
  const d = detail.value!;
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={() => (detail.value = null)}
    >
      <div
        class="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <StudentDrawerBody d={d} />
      </div>
    </div>
  );
}

function StudentDrawerBody({ d }: { d: StudentDetail }) {
  const compras = d.compras ?? [];
  const aulas = d.aulas ?? [];
  const totalPago = compras
    .filter((c) => c.status === "paid")
    .reduce((sum, c) => sum + c.valorCents, 0);
  const pendingCompras = compras.filter((c) => c.status === "pending");
  const totalPendente = pendingCompras.reduce((sum, c) => sum + c.valorCents, 0);
  const aulasFuturas = aulas.filter(
    (a) =>
      a.status === "scheduled" && new Date(a.dataHora).getTime() >= Date.now()
  );
  const aulasDadas = aulas.filter((a) => a.status === "realized");
  return (
    <>
      <header class="flex items-center justify-between border-b border-border px-5 py-4">
        <div class="min-w-0">
          <h2 class="text-base font-bold truncate">{d.student.name || "—"}</h2>
          <p class="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
            {d.student.email}
          </p>
        </div>
        <button
          type="button"
          onClick={() => (detail.value = null)}
          title="Fechar"
          aria-label="Fechar"
          class="grid size-8 place-items-center rounded-md border border-border bg-background hover:bg-accent"
        >
          <X width="1em" height="1em" />
        </button>
      </header>

      <div class="flex-1 space-y-5 overflow-y-auto px-5 py-4">
        {/* KPI strip */}
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiBox
            icon={Receipt}
            label="Pago total"
            value={formatBRL(totalPago)}
            tone="emerald"
          />
          <KpiBox
            icon={CreditCard}
            label="Pendente"
            value={
              pendingCompras.length > 0
                ? `${pendingCompras.length}× ${formatBRL(totalPendente)}`
                : "—"
            }
            tone={pendingCompras.length > 0 ? "amber" : "muted"}
          />
          <KpiBox
            icon={CalendarCheck}
            label="Aulas dadas"
            value={String(aulasDadas.length)}
            tone="blue"
          />
          <KpiBox
            icon={CalendarClock}
            label="Próximas"
            value={String(aulasFuturas.length)}
            tone="primary"
          />
        </div>

        {/* Turmas */}
        <Section icon={Users} title="Turmas">
          {d.enrollments.length === 0 ? (
            <p class="text-[11px] italic text-muted-foreground">
              Nenhuma matrícula.
            </p>
          ) : (
            <ul class="space-y-1">
              {d.enrollments.map((e: any) => (
                <li
                  key={e.turmaId}
                  class={`rounded-md border border-border bg-background/60 px-3 py-1.5 text-xs ${
                    e.withdrawnAt || e.turmaArchived ? "opacity-60" : ""
                  }`}
                >
                  <div class="font-semibold">{e.turmaName}</div>
                  {e.turmaCode && (
                    <div class="font-mono text-[10px] text-muted-foreground">
                      {e.turmaCode}
                    </div>
                  )}
                  {e.withdrawnAt && (
                    <span class="text-[10px] text-amber-700 dark:text-amber-300">
                      retirada em{" "}
                      {new Date(e.withdrawnAt).toLocaleDateString("pt-BR")}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Disciplinas com saldo */}
        <Section icon={Library} title="Disciplinas e saldo">
          {d.disciplinas.length === 0 ? (
            <p class="text-[11px] italic text-muted-foreground">
              Sem disciplinas ativas.
            </p>
          ) : (
            <ul class="space-y-1.5">
              {d.disciplinas.map((dx) => (
                <li
                  key={dx.id}
                  class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-2 text-xs"
                >
                  <div class="min-w-0 flex-1">
                    <div class="font-semibold">{dx.name}</div>
                    {dx.code && (
                      <span class="font-mono text-[10px] text-muted-foreground">
                        {dx.code}
                      </span>
                    )}
                  </div>
                  <span class="inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
                    <CreditCard width="0.85em" height="0.85em" />
                    {dx.saldo} crédito{dx.saldo === 1 ? "" : "s"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Pagamentos (compras de pacotes) */}
        <Section icon={Receipt} title={`Pagamentos (${compras.length})`}>
          {compras.length === 0 ? (
            <p class="text-[11px] italic text-muted-foreground">
              Nenhuma compra registrada.
            </p>
          ) : (
            <ul class="space-y-1.5">
              {compras.map((c) => (
                <CompraRow key={c.id} c={c} />
              ))}
            </ul>
          )}
        </Section>

        {/* Aulas (resumo) */}
        <Section icon={CalendarClock} title="Aulas">
          {aulas.length === 0 ? (
            <p class="text-[11px] italic text-muted-foreground">
              Sem aulas registradas.
            </p>
          ) : (
            <div class="space-y-3">
              {aulasFuturas.length > 0 && (
                <div>
                  <h4 class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Próximas ({aulasFuturas.length})
                  </h4>
                  <ul class="space-y-1">
                    {aulasFuturas.slice(0, 5).map((a) => (
                      <AulaCompactLine key={a.id} a={a} />
                    ))}
                  </ul>
                </div>
              )}
              {aulasDadas.length > 0 && (
                <div>
                  <h4 class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Recentes ({aulasDadas.length})
                  </h4>
                  <ul class="space-y-1">
                    {aulasDadas.slice(0, 5).map((a) => (
                      <AulaCompactLine key={a.id} a={a} />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Section>

        {/* Provas */}
        <Section icon={ClipboardList} title="Provas">
          {d.provas.length === 0 ? (
            <p class="text-[11px] italic text-muted-foreground">
              Nenhuma prova.
            </p>
          ) : (
            <ul class="space-y-1">
              {d.provas.map((p: any) => (
                <li
                  key={p.assessmentId}
                  class="flex items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-1.5 text-xs"
                >
                  <span class="min-w-0 flex-1 truncate font-semibold">
                    {p.title}
                  </span>
                  <span class="tabular-nums text-muted-foreground">
                    {p.score ?? "—"}
                  </span>
                  <span class="text-[10px] uppercase text-muted-foreground">
                    {p.submittedAt ? "submetida" : "em curso"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </>
  );
}

function CompraRow({ c }: { c: StudentDetail["compras"][0] }) {
  const [copied, setCopied] = useState<string | null>(null);
  async function copy(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // ignore
    }
  }
  async function confirmPayment() {
    const ok = await openConfirm({
      title: "Confirmar pagamento manualmente?",
      message: "Marca a cobrança como paga. Use só se você tem certeza de que o pagamento foi recebido. O aluno será notificado.",
      confirmLabel: "Confirmar pagamento"
    });
    if (!ok) return;
    try {
      await api.confirmarPagamento(c.id);
      // recarrega o aluno inteiro
      const aluno = detail.value!.student.id;
      const r = await api.getStudent(aluno);
      detail.value = r;
    } catch (e) {
      await openAlert({
        title: "Falha ao confirmar pagamento",
        message: friendlyError(e),
        danger: true
      });
    }
  }
  const tone =
    c.status === "paid"
      ? "border-emerald-500/30 bg-emerald-500/5"
      : c.status === "pending"
      ? "border-amber-500/30 bg-amber-500/5"
      : "border-border bg-background/60 opacity-70";
  return (
    <li class={`rounded-md border px-3 py-2 text-xs ${tone}`}>
      <div class="flex flex-wrap items-center gap-2">
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-1.5">
            <span class="font-bold">{c.pacoteNomeSnap}</span>
            <CompraStatusBadge status={c.status} />
          </div>
          <div class="mt-0.5 text-[11px] text-muted-foreground">
            {c.disciplinaName} · {c.turmaName} · {c.numAulasSnap} aulas ·{" "}
            <strong>{formatBRL(c.valorCents)}</strong>
          </div>
          <div class="mt-0.5 text-[10px] text-muted-foreground">
            criado {new Date(c.createdAt).toLocaleDateString("pt-BR")}
            {c.paidAt && (
              <> · pago {new Date(c.paidAt).toLocaleDateString("pt-BR")}</>
            )}
          </div>
        </div>
        {c.status === "pending" && (
          <div class="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => copy(c.pixBrcode, "pix")}
              title="Copiar Pix copia-e-cola"
              aria-label="Copiar Pix"
              class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent"
            >
              <Copy width="0.85em" height="0.85em" />
              {copied === "pix" ? "Pix copiado!" : "Copiar Pix"}
            </button>
            <button
              type="button"
              onClick={() => copy(c.txid, "txid")}
              title="Copiar txid"
              aria-label="Copiar txid"
              class="grid size-7 place-items-center rounded-md border border-border bg-background text-muted-foreground hover:bg-accent"
            >
              <Receipt width="0.85em" height="0.85em" />
            </button>
            <button
              type="button"
              onClick={confirmPayment}
              title="Confirmar pagamento manualmente"
              class="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-bold text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
            >
              <CheckCircle2 width="0.85em" height="0.85em" />
              Marcar pago
            </button>
          </div>
        )}
      </div>
      {copied === "txid" && (
        <p class="mt-1 text-[10px] text-emerald-700 dark:text-emerald-400">
          txid copiado!
        </p>
      )}
    </li>
  );
}

function AulaCompactLine({ a }: { a: StudentDetail["aulas"][0] }) {
  const dt = new Date(a.dataHora);
  return (
    <li class="flex items-center gap-2 rounded-md border border-border bg-background/60 px-2 py-1 text-[11px]">
      <span class="font-mono tabular-nums text-muted-foreground">
        {dt.toLocaleDateString("pt-BR")} {dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
      </span>
      <span class="truncate">{a.disciplinaName}</span>
      {a.status === "realized" && (
        <span class="ml-auto rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 dark:text-emerald-400">
          dada
        </span>
      )}
      {a.status === "canceled" && (
        <span class="ml-auto rounded bg-muted px-1.5 py-0.5 text-[9px] font-bold text-muted-foreground">
          cancelada
        </span>
      )}
    </li>
  );
}

function CompraStatusBadge({
  status
}: {
  status: "pending" | "paid" | "expired" | "canceled";
}) {
  const map = {
    paid: { cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", label: "Pago" },
    pending: { cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400", label: "Pendente" },
    expired: { cls: "bg-muted text-muted-foreground", label: "Expirado" },
    canceled: { cls: "bg-muted text-muted-foreground", label: "Cancelado" }
  } as const;
  const m = map[status];
  return <span class={`rounded px-1.5 py-0.5 text-[10px] font-bold ${m.cls}`}>{m.label}</span>;
}

function KpiBox({
  icon: Icon,
  label,
  value,
  tone
}: {
  icon: any;
  label: string;
  value: string;
  tone: "primary" | "emerald" | "blue" | "amber" | "muted";
}) {
  const toneMap = {
    primary: "border-primary/30 bg-primary/5 text-primary",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400",
    blue: "border-blue-500/30 bg-blue-500/5 text-blue-700 dark:text-blue-400",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400",
    muted: "border-border bg-background/60 text-muted-foreground"
  } as const;
  return (
    <div class={`rounded-md border px-3 py-2 ${toneMap[tone]}`}>
      <div class="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider opacity-90">
        <Icon width="0.85em" height="0.85em" />
        {label}
      </div>
      <div class="mt-1 text-base font-extrabold tabular-nums text-foreground">
        {value}
      </div>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  children
}: {
  icon: any;
  title: string;
  children: any;
}) {
  return (
    <section>
      <h3 class="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        <Icon width="1em" height="1em" />
        {title}
      </h3>
      {children}
    </section>
  );
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}
